import { LinearTransform, Rect, transformCSS } from '@st/geom'
import { ReactNode } from 'react'
import type { DocumentViewerSend } from '../../document-viewer'
import { PDFLink, PDFPageStructure } from '../../models'
import { CanvasImage } from './canvas-image'

export function MuPDFPageSurface({
  send,
  pageStructure
}: {
  send: DocumentViewerSend
  pageStructure: PDFPageStructure
}) {
  return (
    <RotatedPage rotation={pageStructure.rotation} sourceBounds={pageStructure.sourceBounds}>
      <CanvasImage
        send={send}
        pageNumber={pageStructure.pageNumber}
        className="w-full bg-white/70"
        width={pageStructure.sourceBounds.width}
        height={pageStructure.sourceBounds.height}
      />
      <PDFLinks send={send} links={pageStructure.links} />
    </RotatedPage>
  )
}

/**
 * Given a child page component, this component will rotate clockwise by a given number of degrees (0, 90, 180, 270)
 * and ensure that the child component is aligned with the top-left of the rotated page.
 *
 * The following will happen:
 * - The dimensions of the page will match the rotated bounds (source bounds rotated by the given rotation)
 * - The visual content of the child component will be rotated by the given rotation
 */
function RotatedPage({
  rotation,
  sourceBounds,
  children
}: {
  rotation: number
  sourceBounds: Rect
  children: ReactNode
}) {
  // In order to produce a rotated page that matches both the layout dimensions and the visual content dimensions,
  // we must account for the rotation in two layers:
  // 1) The outer container confirms to the expected layout, meaning it will have the correct dimensions after the rotation
  // 2) The inner content applies the visual transform to actually transform the visual content.
  const rotationTransform = LinearTransform.pageRotate(rotation, sourceBounds)
  const rotatedBounds = LinearTransform.apply(sourceBounds, rotationTransform)

  return (
    <div
      style={{
        width: rotatedBounds.width,
        height: rotatedBounds.height
      }}
    >
      <div
        style={{
          position: 'relative',
          width: sourceBounds.width,
          height: sourceBounds.height,
          ...transformCSS(rotationTransform)
        }}
      >
        {children}
      </div>
    </div>
  )
}

function PDFLinks({ send, links }: { send: DocumentViewerSend; links: PDFLink[] }) {
  return links.map((link, index) => (
    <button
      key={index}
      type="button"
      aria-label="Go to linked location"
      className="absolute cursor-pointer border-0 bg-transparent p-0"
      style={{
        left: link.bounds.x,
        top: link.bounds.y,
        width: link.bounds.width,
        height: link.bounds.height
      }}
      onClick={() =>
        send({
          type: 'requestViewerScroll',
          target: link.destination,
          behavior: 'smooth'
        })
      }
    />
  ))
}
